import React from 'react';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import { ManifestOptionValue } from '../manifest/types';

interface ResolveCheckboxListProps {
  testId: string;
  value: Array<string | number>;
  options: Array<string | number | ManifestOptionValue>;
  onChange: (next: Array<string | number>) => void;
  disabled?: boolean;
}

const normalizeOption = (option: string | number | ManifestOptionValue): ManifestOptionValue =>
  typeof option === 'object' ? option : { value: option, label: String(option) };

/**
 * The `checkbox` control (`shopper_filters`) — several of `options.values` may be
 * selected at once. Also serves `multi_select`: that control type is declared in the
 * manifest's `control_types` vocabulary but not used by any current setting, so there is
 * nothing to pin its own UI against yet. Rendering it as the same checkbox list keeps the
 * declared vocabulary complete without inventing an untested dropdown-multi-select
 * widget — swap it for a real one the day a setting actually adopts `multi_select`.
 */
const ResolveCheckboxList: React.FC<ResolveCheckboxListProps> = ({ testId, value, options, onChange, disabled }) => {
  const normalized = options.map(normalizeOption);
  const toggle = (optionValue: string | number, checked: boolean) => {
    // A stored value whose option has since left the manifest has no row to uncheck, and the
    // backend rejects it, so any toggle also drops it — otherwise it would block Save for good.
    const known = value.filter((v) => normalized.some((option) => option.value === v));
    if (checked) {
      onChange(known.includes(optionValue) ? known : [...known, optionValue]);
    } else {
      onChange(known.filter((v) => v !== optionValue));
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2" data-testid={testId}>
      {normalized.map((option) => (
        <Checkbox
          key={option.value}
          label={option.label}
          checked={value.includes(option.value)}
          onChange={(checked) => toggle(option.value, checked)}
          disabled={disabled}
          data-testid={`${testId}-option-${option.value}`}
        />
      ))}
    </div>
  );
};

export default ResolveCheckboxList;
